import {Alert, Button, FlexColumn, FlexRow, Modal, Scrollbar, Separator} from '@forecast-it/design-system';
import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer} from 'react-relay';
import {graphql} from 'relay-runtime';
import styled from 'styled-components';
import DeleteCustomFieldDropdownOptionMutation from '../../../../../mutations/custom-fields/DeleteCustomFieldDropdownOptionMutation';
import UpdateCustomFieldDropdownOptionsMutation from '../../../../../mutations/custom-fields/UpdateCustomFieldDropdownOptionsMutation';
import InlineLoader from '../../../../shared/components/inline-loader/inline_loader';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';
import Util from '../../../../shared/util/util';
import CustomFieldDetailsSection from '../../components/CustomFieldDetailsSection';
import {AlertMessage, CustomFieldDefinition} from '../../types';
import type {CustomFieldDropdownOptionsModal_viewer$data as CustomFieldDropdownOptionsModal_viewer} from './__generated__/CustomFieldDropdownOptionsModal_viewer.graphql';
import {DropdownOptionItem} from './components/DropdownOptionItem';
import {showDeleteCustomFieldDropdownOptionConfirmationModal} from './DeleteCustomFieldDropdownOptionConfirmationModal';
import {UnsavedDropdownOption, useDropdownOptions} from './hooks/useDropdownOptions';
import {sameOptionName} from './hooks/useOptionListValidator';

type CustomFieldDropdownOptionsModalInput = {
	customFieldDefinition: CustomFieldDefinition;
	customFieldId?: string;
	setAlertMessage: (message: AlertMessage) => void;
};

export const showCustomFieldDropdownOptionsModal = (props: CustomFieldDropdownOptionsModalInput): void =>
	showModal({
		type: MODAL_TYPE.CUSTOM_FIELD_DROPDOWN_OPTIONS_MODAL,
		...props,
	});

const MarginDiv = styled.div`
	margin: 10px 0;
`;

type CustomFieldDropdownOptionsModalProps = ModalProps<
	{
		viewer: CustomFieldDropdownOptionsModal_viewer;
	} & CustomFieldDropdownOptionsModalInput
>;

function useNewOptionState(existingOptions: UnsavedDropdownOption[]) {
	const {formatMessage} = useIntl();
	const [newOption, setNewOption] = useState('');

	const error = useMemo(() => {
		if (!newOption.trim()) {
			return null;
		}
		if (existingOptions.some(o => sameOptionName(o.displayName, newOption))) {
			return formatMessage({
				id: 'settings.custom-fields.dropdown-options.edit_modal.dropdown_option_item.duplicate_error_message',
			});
		}
		return null;
	}, [existingOptions, newOption]);

	return {newOption, setNewOption, error};
}

//This hook enqueues a scroll-down command, so it will happen on a subsequent render.
function useDeferredScroll(deps: React.DependencyList = []) {
	const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
	const ref = React.useRef<HTMLDivElement>(null);

	function scrollToBottom() {
		const currentRef = ref.current;
		if (currentRef) {
			currentRef.scrollTop = currentRef.scrollHeight;
		}
	}

	useEffect(() => {
		if (shouldScrollToBottom && ref.current) {
			scrollToBottom();
			setShouldScrollToBottom(false);
		}
	}, [...deps, shouldScrollToBottom]);

	function deferScrollToBottom() {
		setShouldScrollToBottom(true);
	}

	return {ref, deferScrollToBottom};
}

const CustomFieldDropdownOptionsModalInternal: React.FC<CustomFieldDropdownOptionsModalProps> = ({
	customFieldDefinition,
	customFieldId,
	viewer,
	setAlertMessage: setParentAlert,
	closeModal,
}) => {
	const {formatMessage} = useIntl();
	const loading = customFieldId && viewer.customFieldDefinition?.options === undefined;

	const [modalAlert, setModalAlert] = useState<AlertMessage | null>(null);
	const [saving, setSaving] = useState(false);
	const [hasUncommittedValue, setHasUncommittedValue] = useState(false);

	const {options, addOption, editOption, removeOption} = useDropdownOptions(viewer.customFieldDefinition?.options);
	const {newOption, setNewOption, error: newOptionValidationError} = useNewOptionState(options);

	const {ref: scrollbarRef, deferScrollToBottom} = useDeferredScroll([options.length]);

	useEffect(() => {
		if (newOption.length === 0) {
			setHasUncommittedValue(false);
		}
	}, [newOption, setHasUncommittedValue]);

	const onAddOption = () => {
		addOption({
			displayName: newOption,
			isUsed: false,
		});
		setNewOption('');
		deferScrollToBottom();
	};

	const onRenameOption = (option: UnsavedDropdownOption, value: string) => {
		editOption(option, {displayName: value});
	};

	const onSave = () => {
		if (newOption.length > 0) {
			setHasUncommittedValue(true);
			return;
		}
		setSaving(true);
		Util.CommitMutation(
			UpdateCustomFieldDropdownOptionsMutation,
			{
				customFieldDefinitionId: customFieldDefinition.id,
				options: options.map(({id, displayName}) => ({
					id,
					displayName,
				})),
			},
			() => {
				setParentAlert({
					text: formatMessage(
						{id: 'settings.custom-fields.update_dropdown_options_alert'},
						{name: customFieldDefinition.displayName}
					),
					type: Alert.TYPE.SUCCESS,
				});
				setSaving(false);
				closeModal();
			},
			true,
			null,
			// The default error handling still runs. Save is re-enabled afterwards so the admin can retry with
			// the same edits, unless the fallback has scheduled its own retry, in which case this attempt is not
			// over yet.
			(transaction: unknown, onFailure: (t: unknown) => boolean | void) => {
				const retryInFlight = onFailure(transaction) === true;
				if (!retryInFlight) {
					setSaving(false);
				}
			}
		);
	};

	const onDeleteOption = (option: UnsavedDropdownOption) => {
		const onDeleteSuccessful = () => {
			removeOption(option);
			setModalAlert({
				text: formatMessage(
					{id: 'settings.custom-fields.dropdown-options.edit_modal.deletion_alert'},
					{displayName: option.displayName}
				),
				type: Alert.TYPE.WARNING,
			});
		};
		const onConfirmDelete = (replacementOptionId: string | null) => {
			if (option.id) {
				Util.CommitMutation(
					DeleteCustomFieldDropdownOptionMutation,
					{
						customFieldDefinitionId: customFieldDefinition.id,
						optionId: option.id,
						replacementOptionId,
					},
					onDeleteSuccessful
				);
			} else {
				onDeleteSuccessful();
			}
		};

		showDeleteCustomFieldDropdownOptionConfirmationModal({
			onConfirm: onConfirmDelete,
			selectedOption: option,
			customFieldId: customFieldDefinition.id,
			entityType: customFieldDefinition.entityType,
		});
	};

	const newOptionError = useMemo(() => {
		if (newOptionValidationError) {
			return newOptionValidationError;
		}
		if (hasUncommittedValue) {
			return formatMessage({
				id: 'settings.custom-fields.dropdown-options.edit_modal.dropdown_option_item.unsaved_value_error_message',
			});
		}
		return null;
	}, [hasUncommittedValue, newOptionValidationError]);

	useEffect(() => {
		if (newOptionError) deferScrollToBottom();
	}, [newOptionError]);

	const saveDisabled = useMemo(
		() => loading || options.some(o => o.error) || !!newOptionError,
		[loading, options, newOptionError]
	);

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>
				{customFieldId ? (
					<FormattedMessage id="settings.custom-fields.dropdown-options.edit_modal.edit_dropdown_options" />
				) : (
					<FormattedMessage id="settings.custom-fields.dropdown-options.edit_modal.add_dropdown_options" />
				)}
			</Modal.Title>
			<Modal.Content>
				{!!modalAlert && <Alert {...modalAlert} />}
				<FlexRow alignItems={'start'} gap={'xl'}>
					<Scrollbar maxHeight={460} ref={scrollbarRef}>
						<FlexColumn gap={'l'} flex={'auto'}>
							{loading && <InlineLoader />}
							{options.map(option => (
								<DropdownOptionItem
									key={option._temporaryId}
									value={option.displayName}
									onChange={value => onRenameOption(option, value)}
									errorText={option.error}
								>
									<Button
										icon="trash"
										emphasis={'medium'}
										onPress={() => onDeleteOption(option)}
										data-cy="delete-dropdown-option"
									/>
								</DropdownOptionItem>
							))}
							{(loading || !!options.length) && (
								<MarginDiv>
									<Separator />
								</MarginDiv>
							)}
							<DropdownOptionItem
								value={newOption}
								onChange={setNewOption}
								errorText={newOptionError}
								isCreateNew
							>
								<Button
									disabled={!newOption || !!newOptionValidationError}
									icon="plus"
									emphasis={'medium'}
									onPress={onAddOption}
								/>
							</DropdownOptionItem>
						</FlexColumn>
					</Scrollbar>
					<CustomFieldDetailsSection customFieldDefinition={customFieldDefinition} />
				</FlexRow>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton disabled={saveDisabled || saving} onPress={onSave}>
					<FormattedMessage id="settings.custom-fields.dropdown-options.edit_modal.save_dropdown_options" />
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const CustomFieldDropdownOptionsModalQuery = graphql`
	query CustomFieldDropdownOptionsModalQuery($customFieldId: ID, $hasCustomFieldId: Boolean!) {
		viewer {
			actualPersonId
			component(name: "custom_field_dropdown_options_modal")
			...CustomFieldDropdownOptionsModal_viewer @arguments(customFieldId: $customFieldId) @include(if: $hasCustomFieldId)
		}
	}
`;

export const CustomFieldDropdownOptionsModal = createFragmentContainer(CustomFieldDropdownOptionsModalInternal, {
	viewer: graphql`
		fragment CustomFieldDropdownOptionsModal_viewer on Viewer @argumentDefinitions(customFieldId: {type: "ID"}) {
			customFieldDefinition(id: $customFieldId) {
				... on DropdownCustomField {
					options {
						id
						displayName
						isUsed
					}
				}
				... on MultiSelectCustomField {
					options {
						id
						displayName
						isUsed
					}
				}
			}
		}
	`,
});
